import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Select, MenuItem,
  FormControl, InputLabel, ToggleButtonGroup, ToggleButton, Button,
  Chip,
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { createWorker, Worker } from 'tesseract.js';
import { isVirtualCamera } from '../utils/camera';

interface PriceScannerProps {
  onPriceDetected: (price: string) => void;
  onError?: (error: string) => void;
  active: boolean;
  resetTrigger?: number;
}

const CAMERA_STORAGE_KEY = 'priceScannerCameraId';
const ZOOM_STORAGE_KEY = 'priceScannerZoom';
const DEFAULT_ZOOM = 2;

// Price extraction patterns
const PRICE_PATTERNS = [
  /\$\s*(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/g,  // $X.XX or $X,XXX.XX
  /(\d{1,3}(?:,\d{3})*\.\d{2})\s*(?:ea|each|\/ea)?/gi,  // X.XX ea
  /(\d{1,2})\s*[.,]\s*(\d{2})\s*(?:¢|cents?)?/g,  // X.XX or X,XX (European)
];

interface CameraDevice {
  deviceId: string;
  label: string;
}

const isMobileDevice = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const stopStream = (stream: MediaStream | null) => {
  stream?.getTracks().forEach(track => track.stop());
};

const PriceScanner: React.FC<PriceScannerProps> = ({ onPriceDetected, onError, active, resetTrigger }) => {
  const scannerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isStoppingRef = useRef(false);
  const isProcessingRef = useRef(false);
  const isInitializingRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waitingForClick, setWaitingForClick] = useState(!isMobileDevice());
  const [backCameras, setBackCameras] = useState<CameraDevice[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [camerasLoaded, setCamerasLoaded] = useState(false);
  const [scannerStarted, setScannerStarted] = useState(false);
  const [maxZoom, setMaxZoom] = useState<number>(1);
  const [currentZoom, setCurrentZoom] = useState<number>(DEFAULT_ZOOM);
  const [availableZoomLevels, setAvailableZoomLevels] = useState<number[]>([1]);

  // Detected prices state
  const [detectedPrices, setDetectedPrices] = useState<string[]>([]);
  const [confirmedPrice, setConfirmedPrice] = useState<string | null>(null);
  const [scanStatus, setScanStatus] = useState<string>('Point camera at price label');

  // Initialize Tesseract worker - only once, terminate only on unmount
  useEffect(() => {
    let mounted = true;

    const initWorker = async () => {
      try {
        const worker = await createWorker('eng');
        if (mounted) {
          workerRef.current = worker;
        } else {
          // Component unmounted during init, clean up
          worker.terminate();
        }
      } catch (err) {
        console.error('Failed to initialize Tesseract:', err);
      }
    };

    if (!workerRef.current) {
      initWorker();
    }

    return () => {
      mounted = false;
      // Only terminate on actual unmount, not on active change
    };
  }, []);

  // Separate cleanup for unmount only
  useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  // Reset detection when resetTrigger changes
  useEffect(() => {
    if (resetTrigger !== undefined && resetTrigger > 0) {
      // Reset all detection state
      setDetectedPrices([]);
      setConfirmedPrice(null);
      setScanStatus('Point camera at price label');
      isProcessingRef.current = false;

      // If scanner was stopped, restart it
      if (!scannerStarted && camerasLoaded && selectedCamera && !waitingForClick && !loading && !isInitializingRef.current && !isStoppingRef.current) {
        startScanner(selectedCamera);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetTrigger]);

  // Load saved zoom
  useEffect(() => {
    try {
      const savedZoom = localStorage.getItem(ZOOM_STORAGE_KEY);
      if (savedZoom) setCurrentZoom(parseFloat(savedZoom));
    } catch (e) { /* ignore */ }
  }, []);

  const stopScanner = async () => {
    if (isStoppingRef.current) return;
    isStoppingRef.current = true;

    // Clear interval first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Wait for any in-progress OCR to complete
    let waitCount = 0;
    while (isProcessingRef.current && waitCount < 20) {
      await new Promise(resolve => setTimeout(resolve, 50));
      waitCount++;
    }

    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch (e) { /* ignore */ }
      videoRef.current.srcObject = null;
    }

    await new Promise(resolve => setTimeout(resolve, 100));

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    videoTrackRef.current = null;
    setScannerStarted(false);
    setLoading(false);
    setError(null);
    setOcrLoading(false);
    isStoppingRef.current = false;
  };

  const resetState = () => {
    setWaitingForClick(!isMobileDevice());
    setCamerasLoaded(false);
    setBackCameras([]);
    setSelectedCamera('');
    setScannerStarted(false);
    setLoading(false);
    setOcrLoading(false);
    setError(null);
    setDetectedPrices([]);
    setConfirmedPrice(null);
    setScanStatus('Point camera at price label');
    videoTrackRef.current = null;
    isProcessingRef.current = false;
  };

  // Camera enumeration
  useEffect(() => {
    const getCameras = async () => {
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
        stopStream(tempStream);
        await new Promise(resolve => setTimeout(resolve, 200));

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices
          .filter(d => d.kind === 'videoinput')
          .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` }));

        const backOnly = videoDevices.filter(d => {
          const label = d.label.toLowerCase();
          if (label.includes('front') || label.includes('user') || label.includes('facetime')) return false;
          if (isVirtualCamera(label)) return false;
          return true;
        });

        const camerasToUse = backOnly.length > 0 ? backOnly : videoDevices;
        setBackCameras(camerasToUse);

        if (camerasToUse.length > 0) {
          const savedCameraId = localStorage.getItem(CAMERA_STORAGE_KEY);
          const savedExists = savedCameraId && camerasToUse.some(c => c.deviceId === savedCameraId);
          if (savedExists) {
            setSelectedCamera(savedCameraId!);
          } else {
            const nonWide = camerasToUse.find(d => !d.label.toLowerCase().includes('wide'));
            setSelectedCamera(nonWide?.deviceId || camerasToUse[0].deviceId);
          }
        }
        setCamerasLoaded(true);
      } catch {
        setCamerasLoaded(true);
      }
    };

    if (active && !camerasLoaded && !isStoppingRef.current && !isInitializingRef.current) {
      getCameras();
    }
  }, [active, camerasLoaded]);

  // Cleanup when deactivated
  useEffect(() => {
    if (!active) {
      const cleanup = async () => {
        // Wait for any initialization to complete first
        let waitCount = 0;
        while (isInitializingRef.current && waitCount < 40) {
          await new Promise(resolve => setTimeout(resolve, 50));
          waitCount++;
        }
        await stopScanner();
        resetState();
      };
      cleanup();
    }
  }, [active]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const handleCameraChange = async (deviceId: string) => {
    await stopScanner();
    setSelectedCamera(deviceId);
    try {
      localStorage.setItem(CAMERA_STORAGE_KEY, deviceId);
    } catch (e) { /* ignore */ }

    if (!waitingForClick) {
      setTimeout(() => startScanner(deviceId), 300);
    }
  };

  const applyZoom = async (zoomLevel: number) => {
    if (!videoTrackRef.current) return;
    try {
      const actualZoom = Math.min(zoomLevel, maxZoom);
      await videoTrackRef.current.applyConstraints({ advanced: [{ zoom: actualZoom } as any] });
      setCurrentZoom(actualZoom);
      try { localStorage.setItem(ZOOM_STORAGE_KEY, actualZoom.toString()); } catch (e) { /* ignore */ }
    } catch (e) { /* ignore */ }
  };

  const handleZoomChange = (_: React.MouseEvent<HTMLElement>, newZoom: number | null) => {
    if (newZoom !== null) applyZoom(newZoom);
  };

  const configureZoom = async (track: MediaStreamTrack) => {
    try {
      videoTrackRef.current = track;
      const capabilities = track.getCapabilities() as any;

      if (capabilities?.zoom) {
        const max = capabilities.zoom.max || 1;
        setMaxZoom(max);
        const levels = [1, 2, 3, 4].filter(z => z <= max);
        setAvailableZoomLevels(levels);

        const savedZoom = localStorage.getItem(ZOOM_STORAGE_KEY);
        const targetZoom = savedZoom ? parseFloat(savedZoom) : DEFAULT_ZOOM;
        const actualZoom = Math.min(targetZoom, max);
        await track.applyConstraints({ advanced: [{ zoom: actualZoom } as any] });
        setCurrentZoom(actualZoom);
      } else {
        setMaxZoom(1);
        setAvailableZoomLevels([1]);
        setCurrentZoom(1);
      }
    } catch (e) {
      setMaxZoom(1);
      setAvailableZoomLevels([1]);
    }
  };

  // Extract prices from OCR text
  const extractPrices = (text: string): string[] => {
    const prices: string[] = [];
    const seen = new Set<string>();

    for (const pattern of PRICE_PATTERNS) {
      // Reset pattern lastIndex for global patterns
      pattern.lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = pattern.exec(text)) !== null) {
        let price: string;

        if (match[2]) {
          // Pattern with two capture groups (X,XX format)
          price = `${match[1]}.${match[2]}`;
        } else if (match[1]) {
          // Pattern with one capture group
          price = match[1].replace(',', '');
        } else {
          continue;
        }

        // Validate price range (0.01 to 9999.99)
        const numPrice = parseFloat(price);
        if (numPrice >= 0.01 && numPrice <= 9999.99) {
          const formatted = numPrice.toFixed(2);
          if (!seen.has(formatted)) {
            seen.add(formatted);
            prices.push(formatted);
          }
        }
      }
    }

    // Sort by likelihood (prefer prices in common ranges)
    return prices.sort((a, b) => {
      const aNum = parseFloat(a);
      const bNum = parseFloat(b);
      // Prefer prices between $0.50 and $50 (most common grocery range)
      const aScore = (aNum >= 0.50 && aNum <= 50) ? 0 : 1;
      const bScore = (bNum >= 0.50 && bNum <= 50) ? 0 : 1;
      return aScore - bScore;
    });
  };

  // Process frame with OCR
  const processFrame = async () => {
    // Check all required refs exist
    if (!videoRef.current || !canvasRef.current) {
      return;
    }

    // Check worker exists and is ready
    if (!workerRef.current) {
      return;
    }

    // Skip if already processing or scanner stopped
    if (isProcessingRef.current || isStoppingRef.current) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (ctx && video.readyState === video.HAVE_ENOUGH_DATA) {
      isProcessingRef.current = true;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      try {
        setOcrLoading(true);
        // Double-check worker still exists before using
        if (workerRef.current) {
          const result = await workerRef.current.recognize(canvas);

          // Check worker still exists after async call
          if (!workerRef.current) return;

          const { data: { text } } = result;

          const prices = extractPrices(text);
          if (prices.length > 0) {
            // Merge with existing prices, keeping unique values
            setDetectedPrices(prev => {
              const merged = new Set([...prev, ...prices]);
              return Array.from(merged).sort((a, b) => {
                const aNum = parseFloat(a);
                const bNum = parseFloat(b);
                const aScore = (aNum >= 0.50 && aNum <= 50) ? 0 : 1;
                const bScore = (bNum >= 0.50 && bNum <= 50) ? 0 : 1;
                return aScore - bScore;
              });
            });
            setScanStatus(`Found ${prices.length} price${prices.length > 1 ? 's' : ''} - tap to select`);
          }
          // Don't clear prices if none found - keep showing previous ones
        }
      } catch (err: any) {
        // Ignore postMessage errors from terminated worker - these are expected
        if (!err?.message?.includes('postMessage') && !err?.message?.includes('terminated')) {
          console.error('OCR error:', err);
        }
      } finally {
        setOcrLoading(false);
        isProcessingRef.current = false;
      }
    }
  };

  const startScanner = async (cameraId?: string) => {
    const deviceId = cameraId || selectedCamera;
    if (!deviceId) {
      setError('No camera selected.');
      return;
    }

    // Prevent multiple simultaneous start attempts
    if (isInitializingRef.current || isStoppingRef.current) {
      return;
    }

    isInitializingRef.current = true;
    setLoading(true);
    setError(null);
    setWaitingForClick(false);
    setDetectedPrices([]);
    setConfirmedPrice(null);
    isProcessingRef.current = false;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: deviceId },
          width: { min: 640, ideal: 1280, max: 1920 },
          height: { min: 480, ideal: 720, max: 1080 },
        },
        audio: false,
      });

      // Check if we should abort (component deactivated during async operation)
      if (isStoppingRef.current) {
        stream.getTracks().forEach(t => t.stop());
        isInitializingRef.current = false;
        setLoading(false);
        return;
      }

      streamRef.current = stream;

      if (videoRef.current) {
        try {
          videoRef.current.pause();
        } catch (e) { /* ignore */ }

        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.muted = true;

        await new Promise<void>((resolve, reject) => {
          const video = videoRef.current!;
          const timeoutId = setTimeout(() => {
            video.removeEventListener('canplay', onCanPlay);
            video.removeEventListener('error', onErrorHandler);
            resolve();
          }, 3000);

          const onCanPlay = () => {
            clearTimeout(timeoutId);
            video.removeEventListener('canplay', onCanPlay);
            video.removeEventListener('error', onErrorHandler);
            resolve();
          };

          const onErrorHandler = () => {
            clearTimeout(timeoutId);
            video.removeEventListener('canplay', onCanPlay);
            video.removeEventListener('error', onErrorHandler);
            reject(new Error('Video failed to load'));
          };

          video.addEventListener('canplay', onCanPlay);
          video.addEventListener('error', onErrorHandler);

          if (video.readyState >= 3) {
            clearTimeout(timeoutId);
            video.removeEventListener('canplay', onCanPlay);
            video.removeEventListener('error', onErrorHandler);
            resolve();
          }
        });

        // Check again if we should abort
        if (isStoppingRef.current) {
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
          }
          isInitializingRef.current = false;
          setLoading(false);
          return;
        }

        await videoRef.current.play();

        const track = stream.getVideoTracks()[0];
        if (track) {
          await configureZoom(track);
        }

        if (!canvasRef.current) {
          canvasRef.current = document.createElement('canvas');
        }

        isInitializingRef.current = false;
        setScannerStarted(true);
        setLoading(false);
        setScanStatus('Point camera at price label');

        // Start OCR processing interval (every 1000ms)
        intervalRef.current = setInterval(processFrame, 1000);
      }
    } catch (err: any) {
      console.error('Scanner error:', err);
      let errorMessage = 'Camera error. Please try again.';

      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera access denied. Please allow camera access.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera found. Please connect a camera.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera is in use or not available.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Camera does not meet requirements. Try another camera.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      isInitializingRef.current = false;
      setError(errorMessage);
      setLoading(false);
      setScannerStarted(false);
      onError?.(errorMessage);
    }
  };

  // Handle price selection
  const handlePriceSelect = (price: string) => {
    setConfirmedPrice(price);
    setScanStatus(`✓ Selected: $${price}`);

    // Stop scanning
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Notify parent after brief delay
    setTimeout(() => onPriceDetected(price), 300);
  };

  // Auto-start scanner
  useEffect(() => {
    if (active && camerasLoaded && selectedCamera && !waitingForClick && !scannerStarted && !loading && !confirmedPrice && !isInitializingRef.current && !isStoppingRef.current) {
      // Small delay to ensure any cleanup from previous session is complete
      const timeoutId = setTimeout(() => {
        if (!isStoppingRef.current && !isInitializingRef.current) {
          startScanner(selectedCamera);
        }
      }, 150);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, camerasLoaded, selectedCamera, waitingForClick, scannerStarted, loading, confirmedPrice]);

  if (!active) return null;

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={() => { setError(null); setWaitingForClick(true); }} sx={{ mb: 2 }}>
          Try Again
        </Button>
        {backCameras.length > 1 && (
          <FormControl fullWidth size="small">
            <InputLabel>Try a different camera</InputLabel>
            <Select
              value={selectedCamera}
              label="Try a different camera"
              onChange={(e) => { setError(null); handleCameraChange(e.target.value); }}
            >
              {backCameras.map((cam) => (
                <MenuItem key={cam.deviceId} value={cam.deviceId}>{cam.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    );
  }

  const showCameraSelector = backCameras.length > 1;
  const showZoomControls = availableZoomLevels.length > 1 && scannerStarted;

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      {showCameraSelector && (
        <Box sx={{ mb: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Camera</InputLabel>
            <Select value={selectedCamera} label="Camera" onChange={(e) => handleCameraChange(e.target.value)}>
              {backCameras.map((cam) => (
                <MenuItem key={cam.deviceId} value={cam.deviceId}>{cam.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Having trouble scanning? Try a different camera.
          </Typography>
        </Box>
      )}

      {waitingForClick && camerasLoaded && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Button variant="contained" startIcon={<VideocamIcon />} onClick={() => startScanner(selectedCamera)} size="large">
            Start Camera
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click to scan price labels
          </Typography>
        </Box>
      )}

      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, bgcolor: 'grey.100', borderRadius: 1 }}>
          <CircularProgress size={32} sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary">Starting camera...</Typography>
        </Box>
      )}

      {!camerasLoaded && !loading && !error && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, bgcolor: 'grey.100', borderRadius: 1 }}>
          <CircularProgress size={32} sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary">Detecting cameras...</Typography>
        </Box>
      )}

      {/* Video container */}
      <Box
        ref={scannerRef}
        sx={{
          position: 'relative',
          display: scannerStarted ? 'block' : 'none',
          width: '100%',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <video
          ref={videoRef}
          style={{
            display: 'block',
            width: '100%',
            maxHeight: 250,
            objectFit: 'cover',
          }}
          playsInline
          muted
        />

        {/* Overlay */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <Box sx={{
            width: '80%',
            height: 80,
            border: '2px solid',
            borderColor: confirmedPrice ? 'success.main' : 'primary.main',
            borderRadius: 1,
            position: 'relative',
          }}>
            {!confirmedPrice && (
              <Box sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                height: 2,
                bgcolor: 'warning.main',
                animation: 'scan 2s linear infinite',
                '@keyframes scan': { '0%': { top: 0 }, '50%': { top: '100%' }, '100%': { top: 0 } }
              }} />
            )}
          </Box>
        </Box>

        {/* OCR loading indicator */}
        {ocrLoading && (
          <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
            <CircularProgress size={20} sx={{ color: 'white' }} />
          </Box>
        )}

        {/* Status */}
        <Typography sx={{
          position: 'absolute',
          bottom: 8,
          left: 0,
          right: 0,
          textAlign: 'center',
          color: 'white',
          textShadow: '0 1px 3px rgba(0,0,0,0.9)',
          fontSize: 14,
          fontWeight: 'bold',
          pointerEvents: 'none'
        }}>
          {scanStatus}
        </Typography>

        {/* Zoom controls */}
        {showZoomControls && (
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <ToggleButtonGroup
              value={currentZoom}
              exclusive
              onChange={handleZoomChange}
              size="small"
              sx={{
                bgcolor: 'rgba(0,0,0,0.6)',
                '& .MuiToggleButton-root': {
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.3)',
                  px: 1,
                  py: 0.5,
                  minWidth: 36,
                  fontSize: 12,
                  '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                },
              }}
            >
              {availableZoomLevels.map((level) => (
                <ToggleButton key={level} value={level}>{level}x</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}
      </Box>

      {/* Detected prices */}
      {scannerStarted && detectedPrices.length > 0 && !confirmedPrice && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Detected Prices (tap to select):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {detectedPrices.map((price, index) => (
              <Chip
                key={index}
                label={`$${price}`}
                onClick={() => handlePriceSelect(price)}
                color="primary"
                variant="outlined"
                sx={{ fontSize: '1rem', py: 2 }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Confirmed price */}
      {confirmedPrice && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Chip
            icon={<CheckCircleIcon />}
            label={`$${confirmedPrice}`}
            color="success"
            sx={{ fontSize: '1.2rem', py: 3, px: 2 }}
          />
        </Box>
      )}
    </Box>
  );
};

export default PriceScanner;
